import { motion, useReducedMotion } from 'framer-motion'
import { useMemo } from 'react'

import { cn } from '@/lib/cn'

/**
 * Le décor des moments de fête à l'écran : une guirlande de fanions et une
 * pluie d'or. De la fête, pas un sapin de Noël — deux couleurs de la charte,
 * un mouvement lent.
 *
 * La guirlande est immobile ; seule la pluie bouge, et elle se tait pour qui
 * a demandé moins d'animations (réglage système du navigateur de la télé).
 * Rien d'informatif ne passe par l'animation.
 */

/** Les couleurs des fanions, dans l'ordre : or, braise, or pâle. */
const PENNANTS = ['fill-champagne-300', 'fill-ember-400', 'fill-champagne-500']

/** Un point d'une courbe de Bézier quadratique, à l'instant `t` (0 à 1). */
function bezier(t: number, a: number, b: number, c: number): number {
  return (1 - t) * (1 - t) * a + 2 * (1 - t) * t * b + t * t * c
}

/**
 * Une guirlande : `swags` arcs de ficelle, des fanions accrochés dessous.
 *
 * Dessinée en SVG, à la largeur de son conteneur : elle garde ses
 * proportions sur une télé 1920 comme sur un vieux 1280.
 */
export function Garland({ swags, className }: { swags: number; className?: string }) {
  const width = swags * 100
  const pennants = useMemo(() => {
    const items: { id: string; x: number; y: number; color: string }[] = []
    for (let swag = 0; swag < swags; swag += 1) {
      const start = swag * 100
      ;[0.2, 0.4, 0.6, 0.8].forEach((t, index) => {
        items.push({
          id: `${swag}-${index}`,
          x: bezier(t, start, start + 50, start + 100),
          y: bezier(t, 4, 30, 4),
          color: PENNANTS[(swag * 4 + index) % PENNANTS.length] ?? PENNANTS[0]!,
        })
      })
    }
    return items
  }, [swags])
  const string = Array.from(
    { length: swags },
    (_, swag) => `${swag === 0 ? 'M0,4 ' : ''}Q${swag * 100 + 50},30 ${(swag + 1) * 100},4`,
  ).join(' ')

  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${width} 36`}
      className={cn('block w-full overflow-visible', className)}
    >
      {/* La ficelle : la couleur passe par `style`, une variable CSS ne
          prend pas de modificateur d'opacité Tailwind. */}
      <path
        d={string}
        fill="none"
        strokeWidth={1.2}
        style={{ stroke: 'var(--champagne-400)', strokeOpacity: 0.75 }}
      />
      {pennants.map((pennant) => (
        <path
          key={pennant.id}
          d={`M${pennant.x - 5.5},${pennant.y} L${pennant.x + 5.5},${pennant.y} L${pennant.x},${pennant.y + 13} Z`}
          className={pennant.color}
        />
      ))}
      {Array.from({ length: swags + 1 }, (_, peak) => (
        <circle key={peak} cx={peak * 100} cy={4} r={2.6} className="fill-champagne-300" />
      ))}
    </svg>
  )
}

/**
 * Des paillettes d'or et de braise qui tombent lentement — toujours aux
 * mêmes places pour une même annonce : un rafraîchissement de la télé ne les
 * fait pas « sauter ».
 */
export function GoldenRain({ seed, count = 28 }: { seed: string; count?: number }) {
  const reduce = useReducedMotion()
  const flakes = useMemo(() => {
    let state = [...seed].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0, 7)
    const next = () => {
      state = (state * 1664525 + 1013904223) >>> 0
      return state / 2 ** 32
    }
    return Array.from({ length: count }, (_, index) => ({
      id: index,
      left: next() * 100,
      size: 0.6 + next() * 1.2,
      delay: next() * 6,
      duration: 7 + next() * 6,
      drift: (next() - 0.5) * 8,
      // Une paillette sur trois est un petit confetti couleur braise.
      ember: index % 3 === 0,
    }))
  }, [seed, count])

  if (reduce) return null
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {flakes.map((flake) => (
        <motion.span
          key={flake.id}
          className={cn(
            'absolute top-0',
            flake.ember ? 'rounded-sm bg-ember-400' : 'rounded-full bg-champagne-300',
          )}
          style={{ left: `${flake.left}%`, width: `${flake.size}vh`, height: `${flake.size}vh` }}
          initial={{ y: '-5vh', x: 0, opacity: 0, rotate: 0 }}
          animate={{
            y: '105vh',
            x: `${flake.drift}vw`,
            opacity: [0, 0.8, 0.8, 0],
            rotate: flake.ember ? 360 : 0,
          }}
          transition={{
            duration: flake.duration,
            delay: flake.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  )
}
